import {
  LEGACY_TRACK_CATALOG,
  LEGACY_TRACK_IDS,
  type LegacyTrackId,
} from "./legacyTrackCatalog";

/**
 * Runtime IDs for every legacy source course which remains retained for
 * adapters, diagnostics, and explicitly reviewed fixtures.  Retention is
 * deliberately separate from shipping eligibility: preserving the source
 * catalog must not put an unqualified course back into the normal game.
 */
export type RetainedLegacyTrackId = `classic-${LegacyTrackId}`;

export const RETAINED_LEGACY_TRACK_IDS: readonly RetainedLegacyTrackId[] = Object.freeze(
  LEGACY_TRACK_CATALOG.map(track => `classic-${track.id}` as RetainedLegacyTrackId),
);

/**
 * Legacy runtime IDs approved for normal shipping surfaces.
 *
 * This starts empty on purpose.  A course can be added only after its 3D
 * route/corner and border behaviour has an owner-accepted qualification;
 * retaining the source data is not itself that qualification.
 */
export const SHIPPING_ELIGIBLE_LEGACY_TRACK_IDS: readonly RetainedLegacyTrackId[] = Object.freeze([]);

const retainedLegacyTrackIds = new Set<string>(RETAINED_LEGACY_TRACK_IDS);
const shippingEligibleLegacyTrackIds = new Set<string>(SHIPPING_ELIGIBLE_LEGACY_TRACK_IDS);
const retainedLegacySourceIds = new Set<string>(LEGACY_TRACK_IDS);

/** True for a runtime `classic-*` ID backed by the retained v1 source. */
export function isRetainedLegacyTrackId(value: unknown): value is RetainedLegacyTrackId {
  return typeof value === "string" && retainedLegacyTrackIds.has(value);
}

/** True for a raw v1 source key such as `bartop` or `velvet`. */
export function isRetainedLegacySourceId(value: unknown): value is LegacyTrackId {
  return typeof value === "string" && retainedLegacySourceIds.has(value);
}

/** True only for a retained legacy runtime ID approved for normal shipping. */
export function isShippingEligibleLegacyTrackId(value: unknown): value is RetainedLegacyTrackId {
  return typeof value === "string" && shippingEligibleLegacyTrackIds.has(value);
}

/**
 * Normal track policy for callers that also handle authored/custom IDs.
 * Unknown IDs are not classified as legacy here; their normal track/schema
 * validators remain responsible for rejecting malformed or unregistered IDs.
 */
export function isShippingEligibleTrackId(value: unknown): boolean {
  return typeof value === "string"
    && (!isRetainedLegacyTrackId(value) || isShippingEligibleLegacyTrackId(value));
}

/**
 * Filter a catalog for a normal shipping surface without mutating its source
 * array.  The full legacy catalog remains available to QA/source diagnostics.
 */
export function filterShippingEligibleLegacyTracks<T extends { readonly id: string }>(tracks: readonly T[]): T[] {
  return tracks.filter(track => isShippingEligibleLegacyTrackId(track.id));
}
